import apiClient, { attachAuthInterceptor } from './api';
import axios from 'axios';

const REPORTING_API_URL = process.env.REACT_APP_REPORTING_URL || '';
const BASE = '/reporting';

const reportingClient = REPORTING_API_URL
  ? (() => {
      const client = axios.create({
        baseURL: `${REPORTING_API_URL}/api`,
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });
      client.interceptors.request.use((config) => {
        const token = localStorage.getItem('access_token');
        if (token) config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
      attachAuthInterceptor(client);
      return client;
    })()
  : apiClient;

const analyticsService = {
  fetchReport: async (endpoint, params = {}) => {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') qp.append(k, String(v));
    });
    const url = `/analytics/${endpoint}${qp.toString() ? '?' + qp.toString() : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  fetchFilterOptions: async (endpoint) => {
    const response = await apiClient.get(`/analytics/${endpoint}`);
    return response.data;
  },

  fetchSources: async () => {
    const response = await reportingClient.get(`${BASE}/sources/`);
    return response.data;
  },

  executeQuery: async (queryBody, signal) => {
    const response = await reportingClient.post(`${BASE}/query/`, queryBody, { signal });
    return response.data;
  },

  fetchFieldValues: async (source, field, search = '') => {
    const qp = new URLSearchParams({ source, field });
    if (search) qp.append('search', search);
    const response = await reportingClient.get(`${BASE}/field-values/?${qp.toString()}`);
    return response.data.values || [];
  },

  listSavedReports: async (params = {}) => {
    const qp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') qp.append(k, String(v));
    });
    const response = await reportingClient.get(`${BASE}/saved-reports/?${qp.toString()}`);
    return response.data;
  },

  getSavedReport: async (id) => {
    const response = await reportingClient.get(`${BASE}/saved-reports/${id}/`);
    return response.data;
  },

  createSavedReport: async (data) => {
    const response = await reportingClient.post(`${BASE}/saved-reports/`, data);
    return response.data;
  },

  updateSavedReport: async (id, data) => {
    const response = await reportingClient.patch(`${BASE}/saved-reports/${id}/`, data);
    return response.data;
  },

  deleteSavedReport: async (id) => {
    await reportingClient.delete(`${BASE}/saved-reports/${id}/`);
  },

  cloneSavedReport: async (id) => {
    const response = await reportingClient.post(`${BASE}/saved-reports/${id}/clone/`);
    return response.data;
  },

  shareSavedReport: async (id, shareData) => {
    const response = await reportingClient.patch(`${BASE}/saved-reports/${id}/share/`, shareData);
    return response.data;
  },

  executeSavedReport: async (id, params = {}) => {
    const response = await reportingClient.post(`${BASE}/saved-reports/${id}/execute/`, params);
    return response.data;
  },

  fetchGroups: async (reportId = null) => {
    const qs = reportId ? `?report=${encodeURIComponent(reportId)}` : '';
    const response = await reportingClient.get(`${BASE}/saved-reports/share-groups/${qs}`);
    return response.data;
  },

  fetchDataFreshness: async () => {
    const response = await reportingClient.get(`${BASE}/data-freshness/`);
    return response.data;
  },

  refreshSource: async (source) => {
    const response = await reportingClient.post(`${BASE}/refresh-source/`, { source });
    return response.data;
  },

  executeCustomQuery: async (sql, limit = 0) => {
    const response = await reportingClient.post(`${BASE}/custom-query/`, { sql, limit });
    return response.data;
  },

  listSchedules: async (reportId) => {
    const qp = reportId ? `?report=${reportId}` : '';
    const response = await reportingClient.get(`${BASE}/report-schedules/${qp}`);
    return response.data;
  },

  createSchedule: async (data) => {
    const response = await reportingClient.post(`${BASE}/report-schedules/`, data);
    return response.data;
  },

  updateSchedule: async (id, data) => {
    const response = await reportingClient.patch(`${BASE}/report-schedules/${id}/`, data);
    return response.data;
  },

  deleteSchedule: async (id) => {
    await reportingClient.delete(`${BASE}/report-schedules/${id}/`);
  },

  runScheduleNow: async (id) => {
    const response = await reportingClient.post(`${BASE}/report-schedules/${id}/run-now/`);
    return response.data;
  },

  searchShareUsers: async (reportId, query) => {
    const response = await reportingClient.get(
      `${BASE}/saved-reports/${reportId}/share-users/?search=${encodeURIComponent(query)}`
    );
    return response.data;
  },

  searchUsers: async (query) => {
    const response = await apiClient.get(`/users/list/?search=${encodeURIComponent(query)}`);
    return response.data;
  },

  // ----- Velocity classification (Slice 3) -----
  getVelocityConfig: async () => {
    const response = await apiClient.get('/analytics/velocity/config/');
    return response.data;
  },

  updateVelocityConfig: async (payload) => {
    const response = await apiClient.patch('/analytics/velocity/config/', payload);
    return response.data;
  },

  getProductVelocity: async (productId) => {
    const response = await apiClient.get(`/analytics/velocity/products/${productId}/`);
    return response.data;
  },

  getProductAvailability: async (productId, { start, end } = {}) => {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const qs = params.toString();
    const url = `/analytics/velocity/products/${productId}/availability/${qs ? '?' + qs : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  getProductSalesTrend: async (productId, { granularity = 'daily', window = '30d' } = {}) => {
    const params = new URLSearchParams({ granularity, window });
    const url = `/analytics/velocity/products/${productId}/sales-trend/?${params.toString()}`;
    const response = await apiClient.get(url);
    return response.data;
  },

  getBulkProductVelocity: async (productIds, { window = '30d' } = {}) => {
    if (!productIds || productIds.length === 0) {
      return { window, products: {} };
    }
    const params = new URLSearchParams({ ids: productIds.join(','), window });
    const response = await apiClient.get(`/analytics/velocity/products/?${params.toString()}`);
    return response.data;
  },

  getProductTopCustomers: async (productId, { window = '30d', limit = 10 } = {}) => {
    const params = new URLSearchParams({ window, limit: String(limit) });
    const response = await apiClient.get(
      `/analytics/velocity/products/${productId}/top-customers/?${params.toString()}`
    );
    return response.data;
  },

  getProductClassHistory: async (productId, { window = '30d', limit = 100 } = {}) => {
    const params = new URLSearchParams({ window, limit: String(limit) });
    const response = await apiClient.get(
      `/analytics/velocity/products/${productId}/class-history/?${params.toString()}`
    );
    return response.data;
  },
};

export default analyticsService;
